import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Flex, theme } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import { useDisconnectGscPropertyMutation } from '../api/gscConnectionApi';
import {
  GscDomainPickerCard,
  GscDomainPickerFooter,
  GscPropertyPickerList,
  GscReplacePropertyModal,
  SearchPerformanceDomainDeadEnd,
  useGscDomainPicker,
} from '../gsc-connection';
import { INTEGRATIONS_ROUTE } from '../SearchPerformanceRoutes';
import GscManageDomainsSkeleton from './GscManageDomainsSkeleton';

const STRINGS = {
  cardTitle: 'Manage synced domains',
  back: 'Back to Integrations',
  save: 'Save changes',
  tryAgain: 'Try again',
  panelTitle: 'What happens when you sync',
  panelItems: [
    'You choose which of your domains to include',
    'We match each one to your Search Console property',
    'We build your Search Performance report from both sides',
  ],
  saved: 'Synced domains updated.',
  saveFailed: 'Could not update your synced domains. Please try again.',
};

const UPGRADE_CTA_LOCATION = 'GSC Settings - Manage Domains';

type Props = {
  sessionId: string;
  sourceId: string;
  /** The properties syncing upstream right now — the seed and the diff baseline. */
  syncedUrls: string[];
  lostUrls: string[];
  /** `/gsc/status` aggregate; the session's own email is the fallback. */
  connectedAccountEmail: string | null;
  onSaved: (counts: { addedCount: number; removedCount: number }) => void;
  onRetryMint: () => void;
};

function difference(urls: string[], excluded: string[]): string[] {
  const exclude = new Set(excluded);
  return urls.filter((url) => !exclude.has(url));
}

function isSameSet(urls: string[], others: string[]): boolean {
  if (urls.length !== others.length) {
    return false;
  }
  const other = new Set(others);
  return urls.every((url) => other.has(url));
}

/**
 * Split from `GscManageDomains` so the hook mounts only once a session exists
 * — it seeds on first load and reads `committedUrls` as they stand then.
 */
function GscManageDomainsPicker({
  sessionId,
  sourceId,
  syncedUrls,
  lostUrls,
  connectedAccountEmail,
  onSaved,
  onRetryMint,
}: Props) {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const picker = useGscDomainPicker({
    sessionId,
    sourceId,
    committedUrls: syncedUrls,
    lostUrls,
    successMessage: STRINGS.saved,
    onActivated: (pairedUrls) => finishSave(pairedUrls.length),
  });
  const [disconnectProperty] = useDisconnectGscPropertyMutation();
  // RTK `isLoading` tracks only the latest trigger; parallel disconnects would
  // drop the spinner early and let a second click re-issue the save.
  const [isSaving, setIsSaving] = useState(false);
  const isSaveInFlight = isSaving || picker.isActivating || picker.replaceConflicts !== null;

  // Diff against the applied seed, not `syncedUrls`: a duplicate spelling the
  // seed skipped was never ticked, so counting it as removed would disconnect
  // it unseen.
  const liveRemovals = difference(picker.initialSelected, picker.selected);
  const removals = [...liveRemovals, ...picker.lostRemovals];
  const additions = difference(picker.selected, syncedUrls);
  const hasChanges = !isSameSet(picker.selected, picker.initialSelected) || picker.lostRemovals.length > 0;
  // An empty selection cannot be saved: clearing the last domain is what
  // Disconnect on the Integrations card is for.
  const removesOnlyLostRows = liveRemovals.length === 0 && picker.lostRemovals.length > 0;
  const canSave = hasChanges && (picker.selected.length > 0 || removesOnlyLostRows);
  const accountEmail = connectedAccountEmail ?? picker.session?.connectedAccountEmail ?? null;

  function finishSave(addedCount = additions.length) {
    onSaved({ addedCount, removedCount: removals.length });
  }

  // Removals first so a cap swap has a free slot before activate.
  const handleSave = async () => {
    setIsSaving(true);
    try {
      try {
        await Promise.all(removals.map((url) => disconnectProperty({ propertyUrl: url }).unwrap()));
      } catch {
        message.error(STRINGS.saveFailed);
        return;
      }

      // Empty `property_urls` 409s a session that has no work to do.
      if (additions.length > 0) {
        await picker.handleActivate();
        return;
      }
      message.success(STRINGS.saved);
      finishSave();
    } finally {
      setIsSaving(false);
    }
  };

  const replaceOfferModal = (
    <GscReplacePropertyModal
      conflicts={picker.replaceConflicts}
      confirmLoading={picker.isActivating}
      onConfirm={picker.confirmReplace}
      onCancel={picker.dismissReplaceOffer}
    />
  );

  const backButton = (
    <Button
      type="link"
      icon={<ArrowLeftOutlined />}
      style={{ paddingLeft: 0 }}
      disabled={isSaveInFlight}
      onClick={() => navigate(INTEGRATIONS_ROUTE)}
    >
      {STRINGS.back}
    </Button>
  );

  if (picker.isFetching) {
    return (
      <>
        <GscManageDomainsSkeleton />
        {replaceOfferModal}
      </>
    );
  }

  if (picker.deadEndReason) {
    const deadEndReason = picker.deadEndReason;
    // Already connected: retry mint, don't send them through Google again.
    const needsFreshSession = deadEndReason === 'session_expired' || deadEndReason === 'session_load_failed';
    return (
      <>
        <Flex vertical gap={token.marginLG}>
          <SearchPerformanceDomainDeadEnd
            reason={needsFreshSession ? 'session_load_failed' : deadEndReason}
            unmatchedSiteUrls={picker.session?.unmatchedSiteUrls}
          />
          <GscDomainPickerFooter
            back={backButton}
            primary={
              needsFreshSession && (
                <Button type="primary" size="large" onClick={onRetryMint}>
                  {STRINGS.tryAgain}
                </Button>
              )
            }
          />
        </Flex>
        {replaceOfferModal}
      </>
    );
  }

  return (
    <>
      <Flex vertical gap={token.marginLG}>
        <GscDomainPickerCard title={STRINGS.cardTitle} panelTitle={STRINGS.panelTitle} panelItems={STRINGS.panelItems}>
          <GscPropertyPickerList
            picker={picker}
            upgradeCtaLocation={UPGRADE_CTA_LOCATION}
            accountEmail={accountEmail}
            syncedUrls={syncedUrls}
            disabled={isSaveInFlight}
          />
        </GscDomainPickerCard>

        <GscDomainPickerFooter
          back={backButton}
          primary={
            <Button
              type="primary"
              size="large"
              loading={isSaveInFlight}
              disabled={isSaveInFlight || !canSave}
              onClick={handleSave}
            >
              {STRINGS.save}
            </Button>
          }
        />
      </Flex>
      {replaceOfferModal}
    </>
  );
}

export default GscManageDomainsPicker;
